<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useChat } from "@/composables/useSidebar";
import { motion, AnimatePresence } from "motion-v";
import { useRoute } from "vue-router";
import { useDevice } from "@/composables/useDevice";

useHead({
  title: "UpBit | My Page",
  bodyAttrs: {
    class: "overflow-visible",
  },
});

// ===== Composables =====
const { isNotificationOpen, closeNotification, isChatOpen, closeChat } =
  useChat();
const route = useRoute();
const { isMobile } = useDevice();

// ===== 상태 관리 =====
// 모바일에서 PC로 전환되었는지 추적
const wasMobileBefore = ref(isMobile.value);
// 초기 애니메이션 건너뛰기 여부
const skipInitialAnimation = ref(false);

// ===== 계산된 값 =====
// 페이지 전환 모션 설정 (데스크톱 전용)
const pageTransition = computed(() => {
  return {
    // 모바일→PC 전환 시에는 initial 건너뛰기
    initial: skipInitialAnimation.value 
      ? false 
      : { opacity: 0, scale: 0.98, y: 10 },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 22,
        mass: 0.8,
      },
    },
    exit: {
      opacity: 0,
      scale: 1.01,
      transition: { duration: 0.15, ease: "easeIn" },
    },
  };
});

// ===== Watchers =====
// isMobile 변경 감지
watch(isMobile, (newValue, oldValue) => {
  // 모바일에서 PC로 전환된 경우
  if (oldValue && !newValue) {
    skipInitialAnimation.value = true;
  }
  wasMobileBefore.value = newValue;
});

// route.path 변경 시 skipInitialAnimation 리셋
watch(() => route.path, () => {
  if (skipInitialAnimation.value) {
    skipInitialAnimation.value = false;
  }
});
</script>

<template>
  <LayoutHeader />
  <main class="mypage__main">
    <div class="mypage__inner">
      <h2 class="mypage__title">마이페이지</h2>
      <div class="mypage__wrap">
        <LayoutMyPageNav />
        <AnimatePresence v-if="!isMobile" mode="wait">
          <motion.section
            class="mypage"
            :key="route.path"
            :initial="pageTransition.initial"
            :animate="pageTransition.animate"
            :exit="pageTransition.exit"
          >
            <slot />
          </motion.section>
        </AnimatePresence>
        <template v-else>
          <section class="mypage" :key="route.path">
            <slot />
          </section>
        </template>
      </div>
    </div>
  </main>
  <CommonNotification
    :is-open="isNotificationOpen"
    variant="sub"
    @close="closeNotification"
  />
  <CommonChatSection :is-open="isChatOpen" variant="sub" @close="closeChat" />
</template>

<style>
@import "@/assets/scss/pages/mypage.scss";
</style>
